// Table `colSizes` are per-column PIXEL widths in Plate: TableElement renders a
// <colgroup><col style="width: Npx"> from them and the column-resize hook treats
// them as pixels. Legacy templates (e.g. the server-side engagement-letter
// template) and old Slate docs instead stored column widths as PERCENTAGES
// ([50, 50] for two columns, [25, 25, 25, 25] for four). Because the editor's
// table CSS is `table-layout: fixed; width: 100%`, those tiny values render
// fine — the browser ignores widths that sum to ~100px against a ~1037px table
// and distributes the columns evenly — so the bad scale is invisible until you
// drag a resize handle. At that point Plate's resize math mixes the column's
// real rendered width (read from the DOM) with the stored neighbour value
// (`colSizes[i+1]`), and its sum-conserving formula collapses the neighbour to
// ~its tiny stored value on the very first pixel of movement: the column shrinks
// to a few px, `word-break: break-word` wraps its text one or two characters per
// line, and the row height balloons (RECUR-26249).
//
// Fix the SCALE at load time. A colSizes array whose positive entries sum to
// less than a full-width table cannot be real pixel widths — a sub-width table
// renders stretched-to-fill under `table-layout: fixed; width: 100%` regardless,
// so its stored values are only ever proportions. Rescale them proportionally to
// the editor content width so the stored sizes match what actually renders and
// the resize hook stays stable.
//
// Arrays summing to MORE than the content width are also rescaled down to fit —
// composed templates (e.g. a year-matrix financial table with many year columns)
// must not open with a horizontal scrollbar. The one exception is a table whose
// columns would be crushed below MIN_TABLE_COL_WIDTH_PX by the rescale (a wide
// spreadsheet paste with dozens of columns): fitting it is worse than scrolling,
// so it is left at its authored widths.
//
// Only FULLY-POSITIVE arrays are rescaled. A zero (or invalid) entry is Plate's
// own "auto width" marker — setTableColSize zero-fills the columns the user
// never resized, and TableElement renders them as a bare <col> that shares the
// leftover width. Those arrays are already on the pixel scale: rescaling just
// the positive entries to the content width hands them the whole table and
// renders every auto column at zero width (a one-column resize of a fresh table,
// [300,0,0,0], became [1037,0,0,0]).
//
// Mixed arrays instead get a minimum-width guarantee (ensureMinColWidths): an
// auto column only stays auto while the leftover width actually gives each one
// at least MIN_TABLE_COL_WIDTH_PX to render in. When the positive entries
// already consume the table — e.g. [300,0,500,237], a doc saved after a pre-fix
// column insert spliced a 0 into a fully-sized table — the auto columns render
// at zero width permanently and every later insert next to them "does nothing"
// (RECUR-26249 follow-up: invisible inserted column). Those arrays are repaired:
// autos are materialised at an equal share and the whole array rescaled to fit.

// .content max-width ($editor-content-width = 1229px) minus its 96px horizontal
// padding on each side — the width a `width: 100%` table actually renders at.
// The exact value only needs to be the right order of magnitude: it puts stored
// colSizes on the same pixel scale as the rendered columns so resize deltas track
// the pointer, and `table-layout: fixed` re-normalises the final render anyway.
export const EDITOR_CONTENT_WIDTH_PX = 1037;

// Matches Plate's own minimum resizable column width — a column narrower than
// this is unusable, so a down-scale that would produce one keeps the scroll.
export const MIN_TABLE_COL_WIDTH_PX = 48;

export const isPositiveWidth = (w: unknown): boolean => typeof w === 'number' && Number.isFinite(w) && w > 0;

export function sanitizeTableColSizes(colSizes: unknown, contentWidth = EDITOR_CONTENT_WIDTH_PX): number[] | undefined {
    if (!Array.isArray(colSizes) || colSizes.length === 0) return undefined;
    // Any non-positive/invalid entry marks an auto-width column → pixel scale,
    // no rescale — but the auto columns must still be renderable (see header).
    if (!colSizes.every(isPositiveWidth)) {
        return ensureMinColWidths(colSizes as number[], contentWidth);
    }
    const nums = colSizes as number[];
    const sum = nums.reduce((a, b) => a + b, 0);
    if (sum === contentWidth) return nums;
    const factor = contentWidth / sum;
    if (sum > contentWidth && Math.min(...nums) * factor < MIN_TABLE_COL_WIDTH_PX) return nums;
    const scaled = nums.map((w) => Math.round(w * factor));
    // Rounding can land back on the authored values (sum within ±n/2 px of the
    // content width) — keep the original identity so callers see a no-op.
    return scaled.every((w, i) => w === nums[i]) ? nums : scaled;
}

// The minimum-width guarantee for arrays containing auto (zero/invalid) entries
// — see the header. A healthy auto regime (leftover width ≥ MIN per auto
// column) is returned by identity so callers can treat the result as a no-op.
// A crushed one comes back fully positive: each auto column is materialised at
// an equal share of the content width, then the whole array is rescaled down to
// fit, floored at the minimum (a floor-forced overflow just means the table
// scrolls slightly — strictly better than invisible columns).
export function ensureMinColWidths(
    colSizes: number[],
    contentWidth = EDITOR_CONTENT_WIDTH_PX,
    minWidth = MIN_TABLE_COL_WIDTH_PX,
): number[] {
    if (!Array.isArray(colSizes) || colSizes.length === 0) return colSizes;
    const autoCount = colSizes.filter((w) => !isPositiveWidth(w)).length;
    if (autoCount === 0) return colSizes;
    const positiveSum = colSizes.reduce((a, w) => a + (isPositiveWidth(w) ? w : 0), 0);
    if ((contentWidth - positiveSum) / autoCount >= minWidth) return colSizes;
    const share = Math.max(minWidth, Math.round(contentWidth / colSizes.length));
    const filled = colSizes.map((w) => (isPositiveWidth(w) ? w : share));
    const total = filled.reduce((a, b) => a + b, 0);
    if (total <= contentWidth) return filled;
    const factor = contentWidth / total;
    return filled.map((w) => Math.max(minWidth, Math.floor(w * factor)));
}

// RECUR-27303: pre-fit OOXML column weights (tblGrid twips or tcW pct — both
// homogeneous per table, so proportions are unaffected by which unit) to the
// editor content width BEFORE sanitizeTableColSizes. Feeding raw twips
// straight to sanitizeTableColSizes would risk landing in its down-scale
// passthrough guard (`:71` above) and coming back off-scale; pre-fitting
// always lands the array at ~contentWidth, where that guard can't trigger.
// 0/auto entries are preserved; sanitizeTableColSizes then runs as an
// idempotent backstop (a no-op here, since the array already sums to
// ~contentWidth) and applies the min-width guarantee for any preserved auto
// entries.
export function fitColSizesFromTwips(weights: number[], contentWidth = EDITOR_CONTENT_WIDTH_PX): number[] {
    const positiveSum = weights.reduce((a, w) => a + (isPositiveWidth(w) ? w : 0), 0);
    if (positiveSum <= 0) return weights;
    const fitted = weights.map((w) => (isPositiveWidth(w) ? Math.round((w * contentWidth) / positiveSum) : 0));
    return sanitizeTableColSizes(fitted, contentWidth) ?? fitted;
}

// Column-insert width fix. @platejs/table's insertTableColumn splices a 0 into
// colSizes for the new column (it only assigns a real width when the plugin's
// `initialTableWidth` option is set — which we can't set: withNormalizeTable
// would then rewrite every auto-width 0 entry on each normalize pass, breaking
// the "0 = auto column" convention above). A 0 renders as a bare <col>, which
// only receives LEFTOVER table width — and a loaded table's colSizes sum to
// exactly the content width (sanitizeTableColSizes), so the leftover is 0 and
// the inserted column renders invisible ("add column does nothing").
//
// This mirrors the width math insertTableColumn runs under `initialTableWidth`:
// the new column takes the width of the column previously at its position (its
// right neighbour; left neighbour at the table end), then, if the new total
// exceeds what the table was already allowed to occupy (its own former total,
// or the content width for narrower tables), every column scales down
// proportionally to fit, floored at the minimum usable width. Callers only
// invoke this for fully-positive arrays — an array containing 0s is in the
// auto-width regime, where leftover width exists and the spliced 0 works as-is.
export function redistributeColSizesAfterInsert(
    oldColSizes: number[],
    insertIndex: number,
    contentWidth = EDITOR_CONTENT_WIDTH_PX,
    minWidth = MIN_TABLE_COL_WIDTH_PX,
): number[] {
    const at = Math.max(0, Math.min(insertIndex, oldColSizes.length));
    const newWidth = oldColSizes[at] ?? oldColSizes[at - 1] ?? contentWidth;
    const next = [...oldColSizes.slice(0, at), newWidth, ...oldColSizes.slice(at)];
    const oldTotal = oldColSizes.reduce((a, b) => a + b, 0);
    const newTotal = next.reduce((a, b) => a + b, 0);
    const maxTotal = Math.max(oldTotal, contentWidth);
    if (newTotal <= maxTotal) return next;
    const factor = maxTotal / newTotal;
    return next.map((w) => Math.max(minWidth, Math.floor(w * factor)));
}

// Walk a Plate value and rescale any mis-scaled table `colSizes` in place-safe
// fashion: node objects are only reallocated where a change is actually needed,
// so the caller's input (which may be the original, un-owned prop value) is never
// mutated. Idempotent — running it on already-normalised content is a no-op.
export function normalizeTableColSizesInValue<T>(value: T): T {
    if (!Array.isArray(value)) return value;
    return value.map((node) => normalizeNode(node)) as unknown as T;
}

function normalizeNode(node: any): any {
    if (!node || typeof node !== 'object') return node;
    let next = node;

    if ((node.type === 'table' || node.type === 'tableWithoutHeaders') && Array.isArray(node.colSizes)) {
        const fixed = sanitizeTableColSizes(node.colSizes);
        if (fixed && fixed !== node.colSizes) next = { ...next, colSizes: fixed };
    }

    if (Array.isArray(node.children)) {
        const kids = node.children.map((child: any) => normalizeNode(child));
        if (kids.some((child: any, i: number) => child !== node.children[i])) {
            next = { ...next, children: kids };
        }
    }

    return next;
}
